import classNames from 'classnames';
import { CSSProperties, PropsWithChildren, ReactNode, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

import { fadeIn } from 'atoms/animations/keyframes';
import { CloseButton } from 'atoms/buttons/CloseButton';
import { Vertical } from 'atoms/layout/flex';
import { useIsTabletAndAbove } from 'hooks/device/useIsTabletAndAbove';
import { unitMapping } from 'lib/style';
import { tabletAndAbove } from 'style/mediaQuery';
import { HexColor } from 'style/types';

import { Dialog } from '..';
import { Season26TutorialBackground } from './Season26TutorialBackground';

const paddingInUnit = 2;
const desktopPaddingInUnit = 3;

const floatIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(1.5);
  }
  38%{
    opacity: 0;
    transform: scale(1.5);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const Content = styled(Vertical).attrs({
  center: true,
})`
  position: relative;
  isolation: isolate;
  width: 100%;
  height: 100%;
  padding: calc(${paddingInUnit} * var(--unit));

  @media ${tabletAndAbove} {
    padding: calc(${desktopPaddingInUnit} * var(--unit));
  }
`;
const ItemsWrapper = styled(Vertical)`
  max-width: calc(var(--100vw) - var(--quadruple-unit));
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const InnerItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  max-width: inherit;
  @media ${tabletAndAbove} {
    flex-direction: row;
    align-items: stretch;
    flex-wrap: wrap;
    & > * {
      flex: 1;
    }
  }
`;

const FooterWrapper = styled.div`
  align-self: stretch;
  position: sticky;
  bottom: 0;
  z-index: 1;

  @media ${tabletAndAbove} {
    position: static;
  }

  &.disableSticky {
    position: static;
  }
`;
const FloatIn = styled.div`
  opacity: 0;
  transform: scale(1.5);
  animation: ${floatIn} 500ms cubic-bezier(1, 0, 0, 1) forwards;
  --animation-delay: 1000ms;
  animation-delay: calc(var(--animation-delay) * (var(--offset, 0) + 1));
  @media ${tabletAndAbove} {
    --animation-delay: 500ms;
  }
`;
const FadeIn = styled.div`
  opacity: 0;
  animation: ${fadeIn} 500ms forwards;
  animation-delay: calc(800ms * (var(--offset, 0) + 1));
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;
const HeaderWrapper = styled.div`
  width: 100%;
  position: relative;

  &:before {
    content: '';
    z-index: -1;
    position: absolute;
    top: calc(-${desktopPaddingInUnit} * var(--unit));
    right: calc(-${desktopPaddingInUnit} * var(--unit));
    bottom: 0;
    left: calc(-${desktopPaddingInUnit} * var(--unit));
    background: no-repeat center / cover var(--header-background-url);
  }
`;

const AnimatedItemWrapper = ({
  children,
  index,
}: PropsWithChildren<{ index: number }>) => {
  const ref = useRef<HTMLDivElement>(null);
  const isTabletAndAbove = useIsTabletAndAbove();
  return (
    <FloatIn
      ref={ref}
      style={{ '--offset': `${index + 1}` } as CSSProperties}
      onAnimationEnd={
        isTabletAndAbove
          ? undefined
          : () => {
              ref.current?.scrollIntoView({
                behavior: 'smooth',
              });
            }
      }
    >
      {children}
    </FloatIn>
  );
};

type Props = {
  open: boolean;
  color: HexColor;
  textColor: HexColor;
  titleGroup: ReactNode;
  items: ReactNode[];
  footer: ReactNode;
  onClose?: () => void;
  disableAnimation?: boolean;
  headerBackgroundUrl?: string;
  hideTextureBackground?: boolean;
  disableSticky?: boolean;
  gap?: keyof typeof unitMapping;
};
export const Season26TutorialDialog = ({
  open,
  color,
  textColor,
  titleGroup,
  items,
  footer,
  onClose,
  disableAnimation,
  headerBackgroundUrl,
  hideTextureBackground = false,
  disableSticky,
  gap = 4,
}: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  return (
    <Dialog open={open} fullScreen onClose={onClose} hideHeader>
      <Season26TutorialBackground
        color={color}
        textColor={textColor}
        hideTextureBackground={hideTextureBackground}
      >
        <Content gap={gap}>
          <HeaderWrapper
            style={
              headerBackgroundUrl
                ? ({
                    '--header-background-url': `url(${headerBackgroundUrl})`,
                  } as CSSProperties)
                : undefined
            }
          >
            {disableAnimation ? titleGroup : <FloatIn>{titleGroup}</FloatIn>}
          </HeaderWrapper>
          <ItemsWrapper>
            <InnerItemsWrapper>
              {items.map((item, index): ReactNode => {
                if (disableAnimation) {
                  return item;
                }
                return (
                  <AnimatedItemWrapper
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    index={index}
                  >
                    {item}
                  </AnimatedItemWrapper>
                );
              })}
            </InnerItemsWrapper>
          </ItemsWrapper>
          <FooterWrapper className={classNames({ disableSticky })}>
            {disableAnimation ? (
              footer
            ) : (
              <FadeIn style={{ '--offset': items.length + 2 } as CSSProperties}>
                {footer}
              </FadeIn>
            )}
          </FooterWrapper>
          {!!onClose && (
            <CloseButtonWrapper>
              <CloseButton
                onClose={onClose}
                size={isTabletAndAbove ? 'large' : 'medium'}
              />
            </CloseButtonWrapper>
          )}
        </Content>
      </Season26TutorialBackground>
    </Dialog>
  );
};
